import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sliders, TrendingUp, Wallet } from "lucide-react";
import { ComingSoonPage } from "./StaticPages";

function BenchmarkStats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/public/benchmark-stats").then(r => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const cards = [
    { l: "Record di benchmark", v: s.benchmark_records?.toLocaleString("it-IT"), note: "Validati e abilitati alla produzione" },
    { l: "KPI coperti", v: s.kpi_covered, note: "Per divisione NACE / ATECO" },
    { l: "Divisioni NACE coperte", v: s.nace2_sectors_covered, note: `Fonte: BACH (ECCBSO) · Anno ${s.reference_year}` },
  ];
  return (
    <div className="grid md:grid-cols-3 gap-5 mt-10">
      {cards.map((k, i) => (
        <div key={i} className="ic-card p-6" data-testid={`benchmark-stat-${i}`}>
          <p className="text-xs uppercase tracking-widest text-[#7A7788]">{k.l}</p>
          <p className="text-3xl font-bold text-[#5424D5] mt-2">{k.v}</p>
          <p className="text-xs text-[#7A7788] mt-1">{k.note}</p>
        </div>
      ))}
    </div>
  );
}

export function BenchmarkPage() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-16">
      <span className="ic-badge status-active mb-4"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />Attivo</span>
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Benchmark di Settore</h1>
      <p className="mt-4 text-lg text-[#524F5E] max-w-2xl">Confronta i tuoi numeri con aziende simili per zona, dimensione e codice ATECO.</p>
      <BenchmarkStats />
      <div className="mt-12 ic-card p-8">
        <h3 className="font-bold text-xl">Dati reali, pronti all'uso</h3>
        <p className="text-[#524F5E] mt-2">I benchmark derivano dalla banca dati BACH (ECCBSO) su bilanci armonizzati delle imprese italiane. Per vedere dove si colloca la tua impresa rispetto alla mediana del tuo settore, fai il Business Check: i confronti sono calcolati sui tuoi dati reali.</p>
        <Link to="/business-check" className="ic-btn-primary inline-block mt-5" data-testid="benchmark-cta">Confronta la mia impresa</Link>
      </div>
    </div>
  );
}

export function SimulatorePage() {
  const [p, setP] = useState(5);
  const [c, setC] = useState(-4);
  const [v, setV] = useState(8);
  const base_revenue = 250000;
  const base_margin_pct = 22;
  const new_revenue = base_revenue * (1 + p/100) * (1 + v/100);
  const new_margin = ((base_margin_pct + p * 0.8) - c * 0.5) / 100;
  const utile = new_revenue * new_margin;
  const utile_base = base_revenue * (base_margin_pct/100);
  const delta_utile = utile - utile_base;

  return (
    <div className="max-w-6xl mx-auto px-4 py-16">
      <span className="ic-badge status-active mb-4"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />Attivo</span>
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Simulatore What-If</h1>
      <p className="mt-4 text-lg text-[#524F5E] max-w-2xl">Sposta gli slider: cosa succede al tuo utile se cambi prezzi, costi o volumi?</p>
      <p className="text-xs text-[#7A7788] mt-2">Esempio illustrativo: base annua €250.000, margine 22% — sposta gli slider per vedere l'effetto delle leve.</p>
      <div className="grid lg:grid-cols-2 gap-8 mt-10">
        <div className="ic-card p-8 space-y-8">
          {[
            {l:"Variazione Prezzi vendita",v:p,set:setP,min:-10,max:20,u:"%",id:"price"},
            {l:"Efficientamento Costi fornitori",v:c,set:setC,min:-15,max:10,u:"%",id:"cost"},
            {l:"Crescita Volumi / Clienti",v:v,set:setV,min:-20,max:30,u:"%",id:"volume"},
          ].map(s => (
            <div key={s.id}>
              <div className="flex justify-between mb-2"><span className="text-sm font-semibold">{s.l}</span><span className="text-lg font-bold text-[#5424D5]" data-testid={`sim-${s.id}-value`}>{s.v > 0 ? '+' : ''}{s.v}{s.u}</span></div>
              <input type="range" min={s.min} max={s.max} value={s.v} onChange={e => s.set(Number(e.target.value))} className="w-full accent-[#5424D5]" data-testid={`sim-${s.id}-slider`} />
            </div>
          ))}
        </div>
        <div className="ic-card p-8 bg-[#171522] text-white">
          <p className="text-xs uppercase tracking-widest text-[#FF6A24] font-bold">Impatto stimato annuo</p>
          <div className="mt-6 space-y-6">
            <div><p className="text-white/60 text-sm">Nuovo fatturato</p><p className="text-3xl font-bold">€{new_revenue.toLocaleString('it-IT',{maximumFractionDigits:0})}</p></div>
            <div><p className="text-white/60 text-sm">Utile stimato</p><p className="text-3xl font-bold text-[#FF6A24]">€{utile.toLocaleString('it-IT',{maximumFractionDigits:0})}</p></div>
            <div><p className="text-white/60 text-sm">Delta rispetto a oggi</p><p className={`text-3xl font-bold ${delta_utile >= 0 ? 'text-[#10B981]' : 'text-[#FF6A24]'}`} data-testid="sim-delta">{delta_utile >= 0 ? '+' : ''}€{delta_utile.toLocaleString('it-IT',{maximumFractionDigits:0})}</p></div>
          </div>
          <p className="text-[10px] text-white/40 mt-8 uppercase tracking-widest">Simulazione semplificata a scopo illustrativo</p>
        </div>
      </div>
    </div>
  );
}

export function BusinessPlanPage() {
  return <ComingSoonPage title="Business Plan Professionale" subtitle="Genera in autonomia un piano economico-finanziario triennale conforme agli standard bancari (MCC, microcredito PMI). Executive Summary, CE previsivo, cash-flow e break-even." service="business-plan" />;
}
export function QuantoCostaAprirePage() {
  return <ComingSoonPage title="Quanto Costa Aprire" subtitle="Calcolatore pratico dell'investimento iniziale per aprire pizzeria, bar, gioielleria, impresa edile e altre attività. Investimento minimo, break-even, tempo di rientro, scenari prudente/base/espansivo." service="quanto-costa-aprire" />;
}
export function BreakEvenPage() {
  return <ComingSoonPage title="Break-even Calculator" subtitle="Calcola il punto di pareggio mensile e annuale: il fatturato minimo necessario per coprire costi fissi e variabili. Margine di contribuzione per prodotto e canale." service="break-even" />;
}
export function PricingCalculatorPage() {
  return <ComingSoonPage title="Pricing Calculator" subtitle="Definisci prezzi di vendita che coprono davvero costi, margine target e punto di pareggio — per prodotto, servizio o orario." service="pricing-calculator" />;
}
export function BusinessPlanProfessionalPage() {
  return <ComingSoonPage title="Business Plan Professionale" subtitle="Piano economico-finanziario completo con Executive Summary, market overview, conto economico previsivo, cash-flow, stato patrimoniale e funding requirement — pronto per banche e bandi." service="business-plan-professional" />;
}
export function AiAdvisorPage() {
  return <ComingSoonPage title="AI Advisor 'Chiara'" subtitle="Chiedi consigli in linguaggio naturale sui tuoi numeri: 'Come riduco il food cost del 3%?', 'Posso assumere una persona in più?'. Il tuo consulente PMI sempre disponibile." service="ai-advisor" />;
}
export function SimulazioneInvestimentoPage() {
  return <ComingSoonPage title="Simulazione Investimento" subtitle="Valuta rientro e sostenibilità di un nuovo investimento: macchinari, ristrutturazione, nuovo veicolo. Payback, NPV e impatto sulla cassa." service="simulazione-investimento" />;
}
export function AperturaNuovaSedePage() {
  return <ComingSoonPage title="Apertura Nuova Sede" subtitle="CapEx iniziale, break-even point e fabbisogno finanziario per l'espansione in un nuovo punto vendita o sede operativa." service="apertura-nuova-sede" />;
}
export function CashFlowPage() {
  return <ComingSoonPage title="Cash Flow Forecast" subtitle="Cruscotto previsionale entrate/uscite a 30, 60 e 90 giorni con alert automatici sulle tensioni di liquidità." service="cash-flow" />;
}
export function SimulatoreAssunzionePage() {
  return <ComingSoonPage title="Simulatore Nuova Assunzione" subtitle="Costo reale del dipendente: RAL, contributi INPS/INAIL, TFR e fatturato minimo incrementale per rendere l'assunzione profittevole." service="simulatore-assunzione" />;
}
export function AnalisiFinanziariaPage() {
  return <ComingSoonPage title="Analisi Economico-Finanziaria" subtitle="Riclassificazione del conto economico, indicatori di bilancio e confronto con i valori tipici del tuo settore." service="analisi-finanziaria" />;
}
export function AnalisiBilancioPage() {
  return <ComingSoonPage title="Analisi Bilancio" subtitle="Analisi approfondita degli ultimi anni di bilancio o dichiarazioni dei redditi, con piano di efficientamento dei costi." service="analisi-bilancio" />;
}
